const en = {
  title: 'Notifications',
  description: 'Send a push notification to the app now, or schedule it up to 30 days ahead.',
  sent: 'Notification sent successfully!',
  scheduled: 'Notification scheduled successfully!',
  scheduledUpdated: 'Scheduled notification updated successfully!',
  unknownArtist: 'Unknown Artist',
  pickDateTime: 'Please pick a date and time.',
  invalidDate: 'Invalid date.',
  mustBeFuture: 'The scheduled time must be in the future.',
  mustBeWithin30Days: 'The scheduled time must be within 30 days.',
  sendFailed: 'Failed to send notification. Please try again.',
  sendFailedTitle: 'Notification not sent',
  scheduleFailed: 'Failed to schedule notification. Please try again.',
  scheduleFailedTitle: "Can't schedule the notification",
  cancelConfirm: 'Cancel this scheduled notification?',
  cancelConfirmTitle: 'Cancel scheduled notification',
  cancelConfirmLabel: 'Cancel notification',
  keepIt: 'Keep it',
  cancelFailed: 'Failed to cancel scheduled notification.',
  cancelFailedTitle: "Can't cancel the notification",
  /** The topic every app user gets */
  allUsers: 'All Users',
  /** The people following a show */
  subscribersOf: (artist: string) => `Subscribers of ${artist}`,
  createTopicFailed: 'Failed to create custom topic. Please try again.',
  createTopicFailedTitle: 'Topic not created',
  renameTopicFailed: 'Failed to update topic name. Please try again.',
  renameTopicFailedTitle: 'Topic not renamed',
  linkFailed: 'Failed to generate link',
  linkCopied: 'Link copied to clipboard!',
  copyManually: 'Please select and copy the link manually',
  general: 'General',
  showSubscribers: 'Show Subscribers',
  selectRecipients: 'Select recipients',
  editScheduled: 'Edit scheduled notification',
  newNotification: 'New notification',
  recipients: 'Recipients',
  searchRecipients: 'Type to search recipients...',
  shareLink: 'Share subscription link',
  renameTopic: 'Rename topic',
  noMatch: (filter: string) => `No recipients match "${filter}"`,
  createTopic: (name: string) => `Create custom topic "${name}"`,
  titleLabel: 'Title',
  message: 'Message',
  delivery: 'Delivery',
  sendNow: 'Send now',
  schedule: 'Schedule',
  scheduledTime: 'Scheduled time',
  scheduledTimeHint: 'Up to 30 days in advance. Time is in your local timezone.',
  cancelEdit: 'Cancel edit',
  updateScheduled: 'Update Scheduled Notification',
  scheduleNotification: 'Schedule Notification',
  sendNotification: 'Send Notification',
  preview: 'Preview',
  /** The time on the lock screen preview's notification */
  previewNow: 'now',
  previewBody: 'Your message shows here.',
  scheduledList: 'Scheduled notifications',
  noScheduled: 'No scheduled notifications.',
  recipient: 'Recipient',
  status: 'Status',
  actions: 'Actions',
  /** A scheduled notification's status, from the API: pending, sent, cancelled or failed */
  statusLabel: (status: string) => status,
  sendConfirm: 'Send this notification?',
  recipientsColon: 'Recipients:',
  titleColon: 'Title:',
  messageColon: 'Message:',
  unknown: 'Unknown',
  send: 'Send',
  topicName: 'Topic name',
  shareLinkDescription: (group: string) =>
    `Share this link to users of group "${group}". They will be automatically subscribed to these notifications.`,
  subscriptionLink: 'Subscription link',
  copy: 'Copy',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'programmée',
  sent: 'envoyée',
  cancelled: 'annulée',
  failed: 'échouée',
};

/** "de" before a name, elided before a vowel: d'Angèle, de The Blaze */
const de = (name: string) => (/^[aeiouàâäéèêëîïôöùûü]/i.test(name) ? `d'${name}` : `de ${name}`);

const fr: typeof en = {
  title: 'Notifications',
  description: "Envoyez une notification push aux festivaliers maintenant, ou programmez-la jusqu'à 30 jours à l'avance.",
  sent: 'Notification envoyée\u00a0!',
  scheduled: 'Notification programmée\u00a0!',
  scheduledUpdated: 'Notification programmée mise à jour\u00a0!',
  unknownArtist: 'Artiste inconnu',
  pickDateTime: 'Choisissez une date et une heure.',
  invalidDate: 'Date non valide.',
  mustBeFuture: "La date d'envoi doit être dans le futur.",
  mustBeWithin30Days: "La date d'envoi doit être dans les 30 prochains jours.",
  sendFailed: "La notification n'a pas pu être envoyée. Veuillez réessayer.",
  sendFailedTitle: 'Notification non envoyée',
  scheduleFailed: "La notification n'a pas pu être programmée. Veuillez réessayer.",
  scheduleFailedTitle: 'Impossible de programmer la notification',
  cancelConfirm: 'Annuler cette notification programmée\u00a0?',
  cancelConfirmTitle: 'Annuler la notification programmée',
  cancelConfirmLabel: 'Annuler la notification',
  keepIt: 'La garder',
  cancelFailed: "La notification programmée n'a pas pu être annulée.",
  cancelFailedTitle: "Impossible d'annuler la notification",
  allUsers: 'Tous les festivaliers',
  subscribersOf: artist => `Abonnés ${de(artist)}`,
  createTopicFailed: "Le sujet personnalisé n'a pas pu être créé. Veuillez réessayer.",
  createTopicFailedTitle: 'Sujet non créé',
  renameTopicFailed: "Le sujet n'a pas pu être renommé. Veuillez réessayer.",
  renameTopicFailedTitle: 'Sujet non renommé',
  linkFailed: 'Impossible de générer le lien',
  linkCopied: 'Lien copié dans le presse-papiers\u00a0!',
  copyManually: 'Sélectionnez et copiez le lien manuellement',
  general: 'Général',
  showSubscribers: 'Abonnés au show',
  selectRecipients: 'Choisir les destinataires',
  editScheduled: 'Modifier la notification programmée',
  newNotification: 'Nouvelle notification',
  recipients: 'Destinataires',
  searchRecipients: 'Rechercher des destinataires...',
  shareLink: "Partager le lien d'abonnement",
  renameTopic: 'Renommer le sujet',
  noMatch: filter => `Aucun destinataire ne correspond à «\u00a0${filter}\u00a0»`,
  createTopic: name => `Créer le sujet personnalisé «\u00a0${name}\u00a0»`,
  titleLabel: 'Titre',
  message: 'Message',
  delivery: 'Envoi',
  sendNow: 'Envoyer maintenant',
  schedule: 'Programmer',
  scheduledTime: "Date d'envoi",
  scheduledTimeHint: "Jusqu'à 30 jours à l'avance. L'heure est celle de votre fuseau horaire.",
  cancelEdit: 'Annuler la modification',
  updateScheduled: 'Mettre à jour la notification programmée',
  scheduleNotification: 'Programmer la notification',
  sendNotification: 'Envoyer la notification',
  preview: 'Aperçu',
  previewNow: 'maintenant',
  previewBody: "Votre message s'affiche ici.",
  scheduledList: 'Notifications programmées',
  noScheduled: 'Aucune notification programmée.',
  recipient: 'Destinataire',
  status: 'Statut',
  actions: 'Actions',
  statusLabel: status => STATUS_LABELS[status] ?? status,
  sendConfirm: 'Envoyer cette notification\u00a0?',
  recipientsColon: 'Destinataires\u00a0:',
  titleColon: 'Titre\u00a0:',
  messageColon: 'Message\u00a0:',
  unknown: 'Inconnu',
  send: 'Envoyer',
  topicName: 'Nom du sujet',
  shareLinkDescription: group =>
    `Partagez ce lien avec les personnes du groupe «\u00a0${group}\u00a0». Elles seront automatiquement abonnées à ces notifications.`,
  subscriptionLink: "Lien d'abonnement",
  copy: 'Copier',
};

const messages = { en, fr };
export default messages;
